import { useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { useLiveQuery } from "@tanstack/react-db";
import { businessesCollection, eq } from "@cliqsuite/db";
import { navSections, isNavItemUnlocked, isNavItemActive, type NavItem } from "./navConfig";
import { AppLinksSection } from "./AppLinksSection";
import { useAuth } from "../../lib/auth";
import { useStoreScope } from "../../lib/storeScope";
import { useInstalledModules } from "../../lib/useInstalledModules";
import { initialsOf } from "../../lib/format";
import { useIsDesktop } from "../../hooks/useIsDesktop";
import { usePermissions } from "../../lib/permissions";

interface SidebarProps {
  /** Ignored on desktop (the sidebar is always visible there). */
  mobileOpen: boolean;
  onCloseMobile: () => void;
}

export function Sidebar({ mobileOpen, onCloseMobile }: SidebarProps) {
  const isDesktop = useIsDesktop();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const onAccount = pathname === "/account";
  const { user, logout } = useAuth();
  const { stores, storeId, setStoreId, store } = useStoreScope();
  const [locOpen, setLocOpen] = useState(false);
  const [appsOpen, setAppsOpen] = useState(false);
  // Phone drawer: a section head opens its items in place (null = follow the current page). On desktop the head is a link.
  const [openSection, setOpenSection] = useState<string | null>(null);
  const { modules: installedModules } = useInstalledModules();
  const { can } = usePermissions();
  const businessId = user?.business_id;
  const { data: businesses } = useLiveQuery(
    (q) =>
      businessId
        ? q.from({ b: businessesCollection }).where(({ b }) => eq(b.id, businessId))
        : q.from({ b: businessesCollection }).where(({ b }) => eq(b.id, "__none__")),
    [businessId],
  );
  const businessName = businesses?.[0]?.name ?? "CliqSuite";

  if (!isDesktop && !mobileOpen) return null;

  const close = () => {
    if (!isDesktop) onCloseMobile();
  };

  const sections = navSections
    .map((sec) => ({ ...sec, items: sec.items.filter((item) => isNavItemUnlocked(item, installedModules) && can(item.permission)) }))
    .filter((sec) => sec.items.length > 0);

  // "Your account" is the second tab of Settings (canvas v2-o-account).
  const lit = (it: NavItem) => isNavItemActive(it, pathname) || (onAccount && it.to === "/settings");

  const item = (it: NavItem) => {
    const active = lit(it);
    return (
      <Link key={it.key} to={it.to} onClick={close} className={`o-nav-item${active ? " is-active" : ""}`} aria-current={active ? "page" : undefined}>
        {active && <span className="o-nav-item__bar" />}
        <span className="ms" aria-hidden="true">{it.icon}</span>
        {it.label}
      </Link>
    );
  };

  const content = (
    <>
      <div className="o-brand">
        <span className="o-brand__mark">C</span>
        <span style={{ minWidth: 0 }}>
          <span className="o-brand__name">{businessName}</span>
          <span className="o-brand__role">OWNER</span>
        </span>
      </div>

      {stores.length > 0 && (
        <div style={{ position: "relative" }}>
          <button type="button" aria-label="Switch store" aria-expanded={locOpen} className="o-store-switch" onClick={() => setLocOpen((v) => !v)}>
            <span className="ms" aria-hidden="true">storefront</span>
            <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{store?.name ?? "Select store"}</span>
            <span className="ms" aria-hidden="true">unfold_more</span>
          </button>
          {locOpen && (
            <>
              <div onClick={() => setLocOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 65 }} />
              <div className="o-popover" style={{ top: "calc(100% - 4px)", left: 0, right: 0 }}>
                {stores.map((s) => (
                  <button
                    type="button"
                    key={s.id}
                    className={`o-popover__item${s.id === storeId ? " is-active" : ""}`}
                    onClick={() => {
                      setStoreId(s.id);
                      setLocOpen(false);
                    }}
                  >
                    <span style={{ flex: 1 }}>{s.name}</span>
                    {s.id === storeId && <span className="ms" aria-hidden="true">check</span>}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      <nav aria-label="Owner Portal" className="o-nav">
        {sections.map((sec, idx) => {
          if (!sec.section) return <div key={`top-${idx}`} className="o-nav__group">{sec.items.map(item)}</div>;
          const here = sec.items.some(lit);
          const open = isDesktop || openSection === null ? here : openSection === sec.section;
          const head = (
            <>
              <span className="ms" aria-hidden="true">{sec.icon}</span>
              <span style={{ flex: 1 }}>{sec.section}</span>
              <span className="ms o-nav-head__chev" aria-hidden="true">{open ? "expand_more" : "chevron_right"}</span>
            </>
          );
          return (
            <div key={sec.section}>
              {isDesktop ? (
                <Link to={sec.items[0]!.to} className={`o-nav-head${open ? " is-open" : ""}`} aria-expanded={open}>
                  {head}
                </Link>
              ) : (
                <button type="button" onClick={() => setOpenSection(open ? "" : sec.section)} className={`o-nav-head${open ? " is-open" : ""}`} aria-expanded={open}>
                  {head}
                </button>
              )}
              {open && <div className="o-nav__group o-nav__group--sub">{sec.items.map(item)}</div>}
            </div>
          );
        })}
        <AppLinksSection open={appsOpen} onToggle={() => setAppsOpen((v) => !v)} />
      </nav>

      {/* The way in to "Your account" (canvas v2-o-account). */}
      <div className="o-me">
        <Link to="/account" onClick={close} aria-label="Your account" style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 10, textDecoration: "none", color: "inherit" }}>
          <span className="o-me__avatar">{initialsOf(user?.name ?? "Owner")}</span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span className="o-me__name">{user?.name}</span>
            <span className="o-me__role">{user?.role}</span>
          </span>
        </Link>
        <button type="button" aria-label="Sign out" className="o-me__out" onClick={logout}>
          <span className="ms" aria-hidden="true">logout</span>
        </button>
      </div>
    </>
  );

  if (isDesktop) return <aside className="o-side">{content}</aside>;

  return (
    <>
      <div className="o-side-backdrop" onClick={onCloseMobile} />
      <aside className="o-side o-side--drawer">{content}</aside>
    </>
  );
}
